import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subject, filter } from 'rxjs';
import { environment } from 'src/environments/environment';

declare let gtag: Function;

// Choix de l'utilisateur pour les cookies soumis à consentement (règles CNIL / RGPD / ePrivacy)
export interface CookieConsent {
  version: number;
  date: string;
  analytics: boolean;
  ads: boolean;
}

// À incrémenter quand une nouvelle catégorie ou un nouveau traceur apparaît : le bandeau est alors reproposé
export const CONSENT_VERSION = 1;
// La CNIL recommande de conserver le choix (acceptation comme refus) 6 mois, puis de redemander
export const CONSENT_MAX_AGE_DAYS = 182;
const STORAGE_KEY = 'cookie_consent';
// Ancienne clé : elle valait 'true' que l'utilisateur ait accepté ou refusé, donc inexploitable
const LEGACY_STORAGE_KEY = 'cookies_consented';

// Message de consentement Google (CMP certifiée IAB TCF v2.2, réglée dans AdSense → Confidentialité et messages) :
// il est diffusé par le tag AdSense, qui doit donc être chargé avant le choix du visiteur.
// Une catégorie n'est acceptée que si toutes ses finalités TCF et Google (fournisseur 755) le sont.
const TCF_VERSION = 2;
const GOOGLE_VENDOR_ID = 755;
const ANALYTICS_PURPOSES = [1, 8];
const ADS_PURPOSES = [1, 2, 3, 4, 7];
// Sans réponse de Google dans ce délai (bloqueur de publicité, message non publié…), on revient au bandeau du site
const CMP_TIMEOUT_MS = 3000;

interface TcfData {
  gdprApplies?: boolean;
  eventStatus?: string;
  listenerId?: number;
  purpose?: { consents?: Record<number, boolean> };
  vendor?: { consents?: Record<number, boolean> };
}

@Injectable({
  providedIn: 'root',
})
export class ConsentService {

  private readonly consentSubject = new BehaviorSubject<CookieConsent | null>(null);
  readonly consent$ = this.consentSubject.asObservable();
  private readonly bannerSubject = new BehaviorSubject<boolean>(false);
  readonly bannerVisible$ = this.bannerSubject.asObservable();
  // Demande d'ouverture de la fenêtre « Personnaliser » (lien « Gérer mes cookies » du pied de page…)
  readonly preferencesRequested$ = new Subject<void>();

  private initialized = false;
  private analyticsLoaded = false;
  private adsLoaded = false;
  private pageViewTracking = false;
  private showBanner = true;
  private localConsentStarted = false;
  private cmpStarted = false;
  private cmpActive = false;
  private tcfListenerId?: number;
  private revocationTimer?: number;

  constructor(
    private router: Router,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  get consent(): CookieConsent | null {
    return this.consentSubject.value;
  }

  // Appelé une fois au démarrage (navigateur seulement) : passe par le message Google, sinon par le bandeau du site
  init(showBanner = true): void {
    if (!this.isBrowser || this.initialized) {
      return;
    }
    this.initialized = true;
    this.showBanner = showBanner;
    this.safeRemove(LEGACY_STORAGE_KEY);
    if (showBanner && environment.ADSENSE_CLIENT) {
      this.startCmp();
    } else {
      this.useLocalConsent();
    }
  }

  // Bandeau du site : réapplique un choix encore valide, sinon affiche le bandeau
  private useLocalConsent(): void {
    if (this.cmpActive || this.localConsentStarted) {
      return;
    }
    this.localConsentStarted = true;
    const stored = this.readStored();
    if (stored) {
      this.consentSubject.next(stored);
      this.apply(stored);
    } else {
      this.bannerSubject.next(this.showBanner);
    }
  }

  private startCmp(): void {
    if (this.cmpStarted) {
      return;
    }
    this.cmpStarted = true;
    this.queueTcfListener();
    const script = this.loadScript('https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=' + environment.ADSENSE_CLIENT);
    script.crossOrigin = 'anonymous';
    script.addEventListener('error', () => this.zone.run(() => this.useLocalConsent()));
    this.zone.runOutsideAngular(() => setTimeout(() => this.zone.run(() => this.useLocalConsent()), CMP_TIMEOUT_MS));
  }

  private queueTcfListener(): void {
    const w = window as any;
    w.googlefc = w.googlefc || {};
    w.googlefc.callbackQueue = w.googlefc.callbackQueue || [];
    w.googlefc.callbackQueue.push({ CONSENT_API_READY: () => this.zone.run(() => this.listenToTcf()) });
  }

  private listenToTcf(): void {
    const tcfApi = (window as any).__tcfapi;
    if (typeof tcfApi !== 'function') {
      this.useLocalConsent();
      return;
    }
    if (this.tcfListenerId !== undefined) {
      tcfApi('removeEventListener', TCF_VERSION, () => { }, this.tcfListenerId);
    }
    tcfApi('addEventListener', TCF_VERSION, (tcData: TcfData, success: boolean) => {
      this.zone.run(() => this.onTcfData(tcData, success));
    });
  }

  private onTcfData(tcData: TcfData, success: boolean): void {
    if (!success || !tcData || tcData.gdprApplies !== true) {
      this.useLocalConsent();
      return;
    }
    this.tcfListenerId = tcData.listenerId;
    // Sans statut, Google n'a ni message à afficher ni choix enregistré (message non publié…) :
    // le délai de startCmp ramène au bandeau du site, qui s'efface si le message arrive ensuite
    if (!tcData.eventStatus) {
      return;
    }
    if (tcData.eventStatus === 'cmpuishown') {
      window.clearTimeout(this.revocationTimer);
    }
    if (!this.cmpActive) {
      this.activateCmp();
    }
    if (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') {
      const consent: CookieConsent = {
        version: CONSENT_VERSION,
        // Le TCF n'expose pas la date du choix, conservée par Google
        date: '',
        analytics: this.tcfAccepts(tcData, ANALYTICS_PURPOSES),
        ads: this.tcfAccepts(tcData, ADS_PURPOSES),
      };
      this.consentSubject.next(consent);
      this.apply(consent);
    }
  }

  // Le message Google remplace le bandeau et un éventuel choix local, qui ne s'applique plus
  private activateCmp(): void {
    this.cmpActive = true;
    this.bannerSubject.next(false);
    this.safeRemove(STORAGE_KEY);
    if (this.consent) {
      const previous = this.consent;
      this.consentSubject.next(null);
      this.apply({ ...previous, analytics: false, ads: false });
    }
  }

  private tcfAccepts(tcData: TcfData, purposes: number[]): boolean {
    return !!tcData.vendor?.consents?.[GOOGLE_VENDOR_ID]
      && purposes.every(purpose => !!tcData.purpose?.consents?.[purpose]);
  }

  acceptAll(): void {
    this.save({ analytics: true, ads: true });
  }

  rejectAll(): void {
    this.save({ analytics: false, ads: false });
  }

  save(choice: { analytics: boolean; ads: boolean }): void {
    const consent: CookieConsent = {
      version: CONSENT_VERSION,
      date: new Date().toISOString(),
      analytics: !!choice.analytics,
      ads: !!choice.ads,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch { }
    this.consentSubject.next(consent);
    this.bannerSubject.next(false);
    this.apply(consent);
  }

  // Lien « Gérer mes cookies » : rouvre le message Google quand c'est lui qui recueille le choix
  openPreferences(): void {
    const googlefc = (window as any).googlefc;
    if (this.cmpActive && googlefc?.showRevocationMessage) {
      googlefc.showRevocationMessage();
      // Google recharge alors son script : on se réabonne pour recevoir le nouveau choix
      this.queueTcfListener();
      // Le lien ne doit jamais rester sans effet : si Google n'affiche rien, on ouvre la fenêtre du site
      window.clearTimeout(this.revocationTimer);
      this.revocationTimer = this.zone.runOutsideAngular(() => window.setTimeout(() => this.zone.run(() => {
        if (!document.querySelector('.fc-consent-root')) {
          this.preferencesRequested$.next();
        }
      }), CMP_TIMEOUT_MS));
      return;
    }
    this.preferencesRequested$.next();
  }

  // Masque le bandeau pendant que la fenêtre « Personnaliser » est ouverte ; il revient si elle est fermée sans choix
  setBannerVisible(visible: boolean): void {
    this.bannerSubject.next(visible && !this.consent && !this.cmpActive);
  }

  private readStored(): CookieConsent | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const consent = JSON.parse(raw) as CookieConsent;
      const ageMs = Date.now() - new Date(consent.date).getTime();
      if (consent.version !== CONSENT_VERSION || !(ageMs >= 0) || ageMs > CONSENT_MAX_AGE_DAYS * 24 * 3600 * 1000) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return consent;
    } catch {
      return null;
    }
  }

  // Google Consent Mode v2 : tout est refusé par défaut dans index.html, on ne lève que ce que l'utilisateur a accepté
  private apply(consent: CookieConsent): void {
    if (typeof gtag === 'undefined') {
      return;
    }
    const state = (granted: boolean) => granted ? 'granted' : 'denied';
    gtag('consent', 'update', {
      analytics_storage: state(consent.analytics),
      ad_storage: state(consent.ads),
      ad_user_data: state(consent.ads),
      ad_personalization: state(consent.ads),
    });
    (window as any)['ga-disable-' + environment.GA_TRACKING_ID] = !consent.analytics;

    if (consent.analytics) {
      this.loadAnalytics();
    } else {
      this.deleteCookies(name => name === '_ga' || name.startsWith('_ga_') || name === '_gid');
    }
    if (consent.ads) {
      this.loadAds();
    } else {
      this.deleteCookies(name => name.startsWith('_gcl_') || name === '__gads' || name === '__gpi');
    }
  }

  private loadAnalytics(): void {
    if (this.analyticsLoaded) {
      return;
    }
    this.analyticsLoaded = true;
    this.loadScript('https://www.googletagmanager.com/gtag/js?id=' + environment.GA_TRACKING_ID);
    // Les pages vues sont envoyées à chaque navigation de l'application (SPA), pas par la config ;
    // cookies _ga limités à 13 mois (CNIL) au lieu des 2 ans par défaut
    gtag('config', environment.GA_TRACKING_ID, { send_page_view: false, cookie_expires: 13 * 30 * 24 * 3600 });
    this.sendPageView(this.router.url);
    if (!this.pageViewTracking) {
      this.pageViewTracking = true;
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          if (this.consent?.analytics) {
            this.sendPageView(event.urlAfterRedirects);
          }
        });
    }
  }

  private sendPageView(path: string): void {
    gtag('event', 'page_view', {
      send_to: environment.GA_TRACKING_ID,
      page_path: path,
      page_location: window.location.origin + path,
    });
  }

  private loadAds(): void {
    if (this.adsLoaded) {
      return;
    }
    this.adsLoaded = true;
    if (environment.GOOGLE_ADS_ID) {
      this.loadScript('https://www.googletagmanager.com/gtag/js?id=' + environment.GOOGLE_ADS_ID);
      gtag('config', environment.GOOGLE_ADS_ID);
      if (environment.GOOGLE_ADS_CONVERSION) {
        // Événement repris tel quel de l'ancien index.html (envoyé à chaque chargement du site)
        gtag('event', 'conversion', {
          send_to: environment.GOOGLE_ADS_CONVERSION,
          value: 1.0,
          currency: 'EUR',
          transaction_id: '',
        });
      }
    }
    if (environment.ADSENSE_CLIENT) {
      this.startCmp();
    }
  }

  private loadScript(src: string): HTMLScriptElement {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
    return script;
  }

  // Un cookie n'est supprimé que si on redonne le même domaine que celui qui l'a posé : on essaie chaque niveau
  private deleteCookies(match: (name: string) => boolean): void {
    const names = document.cookie
      .split(';')
      .map(c => c.split('=')[0].trim())
      .filter(name => name && match(name));
    if (!names.length) {
      return;
    }
    const parts = window.location.hostname.split('.');
    const domains = [''];
    for (let i = 0; i < parts.length - 1; i++) {
      domains.push('.' + parts.slice(i).join('.'));
    }
    for (const name of names) {
      for (const domain of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? '; domain=' + domain : ''}`;
      }
    }
  }

  private safeRemove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch { }
  }
}
